/**
 * Display formatting for the progress screens.
 *
 * Hand-rolled rather than `Intl`: the pixel font is subset to uppercase, digits
 * and a short punctuation list, so a locale that decides to separate thousands
 * with a character outside that set renders as tofu. These functions can only
 * produce glyphs the font actually ships.
 */

const MONTHS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

/** Thousands separated by a plain space, as the mockups show them. */
export function groupNumber(value: number): string {
  const rounded = Math.trunc(Math.abs(value));
  const sign = value < 0 ? '-' : '';
  const digits = String(rounded);

  let grouped = '';
  for (let index = 0; index < digits.length; index += 1) {
    const fromEnd = digits.length - index;
    grouped += digits[index];
    if (fromEnd > 1 && fromEnd % 3 === 1) grouped += ' ';
  }

  return sign + grouped;
}

/** `24 AGO`. The year is dropped: the lists it appears in are recent by nature. */
export function shortDate(timestamp: number): string {
  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${MONTHS[date.getMonth()] ?? ''}`;
}

/** `24 AGO 2026`. Used where the date is the point, as on an unlock. */
export function longDate(timestamp: number): string {
  return `${shortDate(timestamp)} ${new Date(timestamp).getFullYear()}`;
}

/**
 * `1ST`, `2ND`, `3RD`, `4TH`. English ordinals in an otherwise Spanish UI
 * because that is the arcade high-score convention the mockups use, and the
 * subset font ships exactly these glyphs.
 *
 * The teens are the whole reason this is a function: 11, 12 and 13 take TH
 * despite ending in 1, 2 and 3.
 */
export function ordinalRank(rank: number): string {
  const value = Math.trunc(Math.abs(rank));
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}TH`;

  const suffix = { 1: 'ST', 2: 'ND', 3: 'RD' }[value % 10] ?? 'TH';
  return `${value}${suffix}`;
}

/** Signed, for a rating movement: `+12`, `-8`, `0`. */
export function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

/** 0-100, for the discrete progress bars. Never overfills, never divides by zero. */
export function percentOf(value: number, goal: number): number {
  if (goal <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / goal) * 100)));
}
